<template>
  <div class="collection-gate-success">
    <h2 class="collection-gate-success__heading">
      {{ heading }}
    </h2>
    <template v-if="showCountdown">
      <p class="collection-gate-success__text">
        {{ countdownText }}
      </p>
      <div class="collection-gate-success__countdown">
        <div class="countdown-unit">
          <span class="countdown-value">{{
            countdown.days.toString().padStart(2, "0")
          }}</span>
        </div>
        <div class="countdown-separator">:</div>
        <div class="countdown-unit">
          <span class="countdown-value">{{
            countdown.hours.toString().padStart(2, "0")
          }}</span>
        </div>
        <div class="countdown-separator">:</div>
        <div class="countdown-unit">
          <span class="countdown-value">{{
            countdown.minutes.toString().padStart(2, "0")
          }}</span>
        </div>
        <div class="countdown-separator">:</div>
        <div class="countdown-unit">
          <span class="countdown-value">{{
            countdown.seconds.toString().padStart(2, "0")
          }}</span>
        </div>
      </div>
    </template>
    <template v-else>
      <p class="collection-gate-success__text">
        {{ successText }}
      </p>
      <div class="collection-gate-success__button">
        <Button
          tag="a"
          href="/collections/the-attimi-capsule"
          theme="primary-light"
        >
          Shop the collection
        </Button>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { format, differenceInSeconds } from "date-fns";
import Button from "@/components/ButtonV3.vue";
import {
  collectionGateAccessTimes,
  getCollectionGateTierLevel,
  getCollectionGateAccessTime,
} from "@/config.js";

export default {
  name: "CollectionGateSuccess",
  components: {
    Button,
  },
  data() {
    return {
      now: new Date(),
      timer: null,
    };
  },
  computed: {
    ...mapState("auth", ["customer"]),
    ...mapGetters("app", ["currencyCode"]),
    userTierLevel() {
      return getCollectionGateTierLevel(this.customer?.tags || []);
    },
    heading() {
      if (this.userTierLevel === 0) {
        return "COLLECTION ACCESS CONFIRMED";
      }
      return `LEVEL ${this.userTierLevel} EARLY ACCESS CONFIRMED`;
    },
    countdownText() {
      if (this.userTierLevel === 0) {
        return "The collection will be available to shop on";
      }
      return "Your early access begins on";
    },
    successText() {
      if (this.userTierLevel === 0) {
        return "The collection is now available to shop";
      }
      return `The collection is now available for Level ${this.userTierLevel} members`;
    },
    targetDate() {
      const currencyAccessTimes =
        collectionGateAccessTimes[this.currencyCode.toLowerCase()] ||
        collectionGateAccessTimes.gbp;
      if (!currencyAccessTimes) {
        return new Date();
      }

      const accessTimeIso = getCollectionGateAccessTime(
        currencyAccessTimes,
        this.userTierLevel
      );
      return new Date(accessTimeIso || currencyAccessTimes.disabled);
    },
    showCountdown() {
      return this.now < this.targetDate;
    },
    countdown() {
      const totalSeconds = differenceInSeconds(this.targetDate, this.now);
      if (totalSeconds <= 0)
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };

      const days = Math.floor(totalSeconds / (24 * 60 * 60));
      const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      return { days, hours, minutes, seconds };
    },
    formattedOpeningDate() {
      return format(this.targetDate, "do MMMM yyyy 'at' h:mm a 'GMT'");
    },
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = new Date();
    }, 1000);
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
};
</script>

<style lang="scss" scoped>
.collection-gate-success {
  text-align: center;
  padding: 20px;

  &__heading {
    @include editorial-headline(24px);
    margin-bottom: 20px;
  }

  &__text {
    @include editorial-body(16px);
    margin-bottom: 30px;
  }

  &__countdown {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-bottom: 30px;
  }

  &__button {
    display: flex;
    justify-content: center;
  }
}

.countdown-unit {
  display: flex;
  align-items: center;
  min-width: 40px;
}

.countdown-value {
  @include editorial-headline(32px);
  line-height: 1;
}

.countdown-separator {
  @include editorial-headline(32px);
  line-height: 1;
  margin-top: -4px;
}
</style>
